import {HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {ICase, IUser} from '@service-and-repairs/awpintegrationlib';
import {firstValueFrom, TimeoutError} from 'rxjs';
import {Util} from '../../../../util/util';
import {AddServiceOrderJobsResponse} from '../models/add-order-jobs';
import {
  AwpDmsInterfaceAddOrderJobsRequestFactory
} from '../models/add-order-jobs/awp-dms-interface-add-order-jobs-request-factory';
import {
  AwpDmsInterfaceCheckHealthRequestFactory
} from '../models/check-health/awp-dms-interface-check-health-request-factory';
import {CheckHealthResponse} from '../models/check-health/check-health-response';
import {ApiResponse} from '../models/common/api-response';
import {DmsRequest} from '../models/common/dms-request';
import {DmsResponse} from '../models/common/dms-response';
import {
  AwpDmsInterfaceGetCustomerRequestFactory,
  AwpDmsInterfaceSearchCustomersRequestFactory,
  GetCustomerResponse,
  SearchCustomersFilter,
  SearchCustomersResponse
} from '../models/customer';
import {GetServiceOrderResponse} from '../models/get-service-order';
import {
  AwpDmsInterfaceGetServiceOrderRequestFactory
} from '../models/get-service-order/awp-dms-interface-get-service-order-request-factory';
import {CreateServiceOrderResponse} from '../models/order';
import {
  AwpDmsInterfaceCreateServiceOrderRequestFactory
} from '../models/order/awp-dms-interface-create-service-order-request-factory';
import {
  AwpDmsInterfacePriceStockRequestFactory
} from '../models/price-stock/awp-dms-interface-price-stock-request-factory';
import {GetPriceAndStockInformationResponse} from '../models/price-stock/get-price-and-stock-information-response';
import {
  AwpDmsInterfaceSearchServiceOrdersRequestFactory,
  SearchServiceOrdersFilter,
  SearchServiceOrdersResponse
} from '../models/search-service-order';
import {
  AwpDmsInterfaceVehicleRequestFactory,
  GetVehicleResponse,
  SearchVehiclesFilter,
  SearchVehiclesResponse
} from '../models/vehicle';

interface NormalizedHttpError {
  status: number;
  message: string;
  details?: unknown;
  retriable: boolean;
  originalError: unknown;
}

@Injectable({providedIn: 'root'})
export class AwpDmsInterfaceService {
  readonly http: HttpClient = inject(HttpClient);
  headers: HttpHeaders = new HttpHeaders({
    'Content-Type': 'application/json',
    'Cavors-Session-Id': sessionStorage.getItem('cavors-session-id')
  });

  private readonly checkHealthFactory = new AwpDmsInterfaceCheckHealthRequestFactory();
  private readonly priceStockFactory = new AwpDmsInterfacePriceStockRequestFactory();
  private readonly createServiceOrderFactory = new AwpDmsInterfaceCreateServiceOrderRequestFactory();
  private readonly addOrderJobsFactory = new AwpDmsInterfaceAddOrderJobsRequestFactory();
  private readonly getServiceOrderFactory = new AwpDmsInterfaceGetServiceOrderRequestFactory();
  private readonly vehicleFactory = new AwpDmsInterfaceVehicleRequestFactory();
  private readonly searchServiceOrdersFactory = new AwpDmsInterfaceSearchServiceOrdersRequestFactory();
  private readonly searchCustomersFactory = new AwpDmsInterfaceSearchCustomersRequestFactory();
  private readonly getCustomerFactory = new AwpDmsInterfaceGetCustomerRequestFactory();
  private readonly endpointMap: Record<string, string> = {
    'check-health': 'check-health',
    'price-stock': 'price-stock',
    'search-customers': 'search-customers',
    'get-customer': 'get-customer',
    'search-vehicles': 'search-vehicles',
    'get-vehicle': 'get-vehicle',
    'get-local-group-list': 'get-local-group-list',
    'get-local-group-details': 'get-local-group-details',
    'create-order': 'create-order',
    'get-order': 'get-order',
    'update-order-metadata': 'update-order-metadata',
    'add-order-jobs': 'add-order-jobs',
    'update-order-jobs': 'update-order-jobs',
    'search-orders': 'search-orders',
    'print-order': 'print-order',
    'confirm-order': 'confirm-order',
    'cancel-order': 'cancel-order'
  } as const;

  public async checkHealth(locale: string, dpNo: string, outletNo: string): Promise<CheckHealthResponse> {
    return this.send(this.checkHealthFactory.createCheckHealthRequest(locale, dpNo, outletNo));
  }

  public async getPriceAndStockInformation(user: IUser,
                                           serviceCase: ICase): Promise<GetPriceAndStockInformationResponse> {
    return this.send(this.priceStockFactory.createRequest(user, serviceCase));
  }

  public async createServiceOrder(user: IUser, serviceCase: ICase): Promise<CreateServiceOrderResponse> {
    return this.send(this.createServiceOrderFactory.createRequest(user, serviceCase));
  }

  public async addServiceOrderJobs(user: IUser, serviceCase: ICase): Promise<AddServiceOrderJobsResponse> {
    return this.send(this.addOrderJobsFactory.createRequest(user, serviceCase));
  }

  public async getServiceOrder(user: IUser, serviceCase: ICase): Promise<GetServiceOrderResponse> {
    return this.send(this.getServiceOrderFactory.createRequest(user, serviceCase));
  }

  public async searchServiceOrders(searchFilter: SearchServiceOrdersFilter,
                                   locale: string,
                                   dpNo: string,
                                   outletNo: string,
                                   resultPartition: number = 10): Promise<SearchServiceOrdersResponse> {
    return this.send(this.searchServiceOrdersFactory.createRequest(
      searchFilter,
      resultPartition,
      locale,
      dpNo,
      outletNo
    ));
  }

  public async getVehicle(vin: string, locale: string, dpNo: string, outletNo: string): Promise<GetVehicleResponse> {
    return this.send(this.vehicleFactory.createGetVehicleRequest(vin, locale, dpNo, outletNo));
  }

  public async searchVehicles(searchFilter: SearchVehiclesFilter,
                              locale: string,
                              dpNo: string,
                              outletNo: string,
                              resultPartition: number = 10): Promise<SearchVehiclesResponse> {
    return this.send(this.vehicleFactory.createSearchVehiclesRequest(
      searchFilter,
      resultPartition,
      locale,
      dpNo,
      outletNo
    ));
  }

  public async searchCustomers(searchFilter: SearchCustomersFilter,
                               locale: string,
                               dpNo: string,
                               outletNo: string,
                               resultPartition: number = 10,
                               vehicleResultPartition: number = 10): Promise<SearchCustomersResponse> {
    return this.send(this.searchCustomersFactory.createRequest(
      searchFilter,
      resultPartition,
      vehicleResultPartition,
      locale,
      dpNo,
      outletNo
    ));
  }

  public async getCustomer(dmsCustomerId: string,
                           locale: string,
                           dpNo: string,
                           outletNo: string,
                           maxVehicleResults: number = 10): Promise<GetCustomerResponse> {
    return this.send(this.getCustomerFactory.createRequest(dmsCustomerId, maxVehicleResults, locale, dpNo, outletNo));
  }

  /**
   * Checks if the DMS service (backend) is reachable.
   * This is a simple connectivity check, not a full health check through the middleware.
   * @returns Resolves if the service is available, rejects with error otherwise.
   */
  public async checkDmsServiceConnection(): Promise<void> {
    const url = '/api/dms/awp-dms-interface/check-dms-service-connection';
    try {
      console.info(`[Webapp] [AwpDmsInterfaceService] GET ${url}`);
      await firstValueFrom(this.http.get(url, {headers: this.headers, withCredentials: true, responseType: 'text'}));
    } catch (err) {
      throw this.normalizeError(err);
    }
  }

  public async send<Req extends DmsRequest, Res extends DmsResponse>(request: Req): Promise<Res> {
    // URL
    const url = this.makeEndpointUrl(request.simpleName);

    // Headers
    this.headers = this.headers.set('Cavors-Request-Id', Util.createRandomUuid());
    request.technicalHeader.requestId = this.headers.get('Cavors-Request-Id') || '';
    request.technicalHeader.sessionId = this.headers.get('Cavors-Session-Id') || '';

    // Request
    try {
      console.info(`[Webapp] [AwpDmsInterfaceService] POST ${url}`);
      // Backend wraps all responses in ApiResponse<T> with { data: T, errors: [] }
      const apiResponse = await firstValueFrom(
        this.http.post<ApiResponse<Res>>(url, request, {headers: this.headers, withCredentials: true})
      );
      // Log backend-level errors if present (distinct from DMS business errors)
      if (apiResponse.errors?.length > 0) {
        console.info('[Webapp] [AwpDmsInterfaceService] Backend returned errors in ApiResponse:', apiResponse.errors);
      }
      return apiResponse.data;
    } catch (err) {
      throw this.normalizeError(err);
    }
  }

  private makeEndpointUrl(simpleRequestName: string): string {
    const pathSegmentForRequest = this.endpointMap[simpleRequestName];
    if (!pathSegmentForRequest) {
      throw new Error(`Unsupported request type: ${simpleRequestName}`);
    }
    return '/api/dms/awp-dms-interface/' + pathSegmentForRequest;
  }

  private normalizeError(error: unknown): NormalizedHttpError {
    if (error instanceof TimeoutError) {
      return {
        status: 0,
        message: 'Request timeout',
        retriable: true,
        originalError: error
      };
    }
    if (error instanceof HttpErrorResponse) {
      return {
        status: error.status,
        message: error.message || 'HTTP error',
        details: error.error,
        retriable: error.status === 0 || (error.status >= 500 && error.status < 600),
        originalError: error
      };
    }
    return {
      status: -1,
      message: 'Unknown error',
      retriable: false,
      originalError: error
    };
  }
}
